/**
 * useCreateChild hook
 * TanStack React Query mutation hook for creating the family's first child profile
 * from the onboarding trial flow (US-005).
 *
 * Implements US-005 (T2): useCreateChild mutation hook.
 *
 * Reuses:
 *   - createChild service from services/children.ts (deployed by US-004/TS-015).
 *   - useMutation + useQueryClient from @tanstack/react-query (useChildMutations.ts pattern).
 *   - ['parent','dashboard'] cache key (useParentDashboard.ts line 74).
 *
 * TO-LL-036 enforcement: errors are never caught/swallowed; mutation.error is exposed
 * so AddFirstChildModal can surface the error inline and keep the form open.
 * TO-LL-044 enforcement: mutation variables use exact camelCase keys (displayName,
 * password, username) via CreateChildPayload type, matching children.py lines 126-128.
 *
 * On success: invalidates ['parent','dashboard'] so useParentDashboard refetches and
 * the trial banner disappears automatically (children.length becomes >= 1).
 * Does NOT navigate anywhere — the modal (T3/AddFirstChildModal) handles post-create UX.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createChild } from '@/services/children';
import type { CreateChildPayload, CreatedChild } from '@/types/children';
import type { AuthError } from '@/types/auth';

// ─── Return type ──────────────────────────────────────────────────────────────

/**
 * Return shape of the useCreateChild hook.
 * Mirrors the ChildMutationSlot interface in useChildMutations for consistency.
 */
export interface UseCreateChildReturn {
  /**
   * Trigger the create-child mutation.
   * TO-LL-044: payload must use camelCase keys (displayName, password, username?)
   */
  create: (payload: CreateChildPayload) => void;
  /** True while the POST /children request is in-flight */
  isPending: boolean;
  /**
   * Error from the axios interceptor ({code, message}) or null when no error.
   * TO-LL-036: never null-ified silently; always surfaced to the modal.
   */
  error: AuthError | null;
  /** True after the mutation succeeded (data is set) */
  isSuccess: boolean;
  /** Reset mutation state — called when modal reopens to clear stale error/data */
  reset: () => void;
  /**
   * The created child data from the 201 response.
   * Used by AddFirstChildModal to show the child's login username in the success view.
   * Shape: { userId, username, displayName, parentUserId } (TO-LL-044: exact camelCase).
   */
  data: CreatedChild | undefined;
}

// ─── Hook implementation ──────────────────────────────────────────────────────

/**
 * useCreateChild — React Query mutation for the US-005 first-child onboarding flow.
 *
 * Distinct from useChildMutations.create (US-004) in that it:
 *  1. Exposes the full CreatedChild response data (needed to show login credentials).
 *  2. Is used exclusively by AddFirstChildModal — not by AddChildDialog.
 *
 * TO-LL-036: onSuccess-only invalidation; a failed mutation never triggers a
 * stale-then-correct refetch that would obscure the error state.
 *
 * @returns UseCreateChildReturn — { create, isPending, error, isSuccess, reset, data }
 */
export function useCreateChild(): UseCreateChildReturn {
  const queryClient = useQueryClient();

  /**
   * Create-child mutation.
   * mutationFn: (payload: CreateChildPayload) => createChild(payload) [services/children.ts]
   * onSuccess: invalidates ['parent','dashboard'] so the trial banner hides and the
   *   new child card appears in ChildProfilesGrid (no manual prop-plumbing required).
   * TO-LL-044: payload keys enforced by CreateChildPayload type.
   * TO-LL-036: no try/catch; errors propagate to mutation.error.
   */
  const mutation = useMutation<CreatedChild, AuthError, CreateChildPayload>({
    mutationFn: (payload) => createChild(payload),
    onSuccess: () => {
      // Targeted single-query invalidation — no full cache flush (perf consideration)
      queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
    },
  });

  return {
    create: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    data: mutation.data,
  };
}
